<template>
  <div class="skeleton-loader">
    <div
      v-if="isValidType('list')"
      class="skeleton__list-items"
      :style="{ width, height }"
    >
      <div
        v-for="i in listItemsAmount('list-items')"
        :key="i"
        class="list-item"
      >
        <div v-if="loading" class="skeleton-loader__wave"></div>
        <div class="list-item__logo content-item"></div>
        <div class="list-item__content">
          <div class="content-item"></div>
          <div v-if="type.includes('info')" class="content-item"></div>
        </div>
      </div>
    </div>
    <div
      v-else-if="type === 'card'"
      class="skeleton__card"
      :style="{ width, height }"
    >
      <div v-if="loading" class="skeleton-loader__wave"></div>
      <div class="card__logo content-item"></div>
      <div class="card__content">
        <div class="content-item"></div>
        <div class="content-item"></div>
      </div>
    </div>
    <div
           v-else-if="isValidType('column')"
      :style="{ width, height }"
      class="skeleton-cols"
    >
      <div v-if="loading" class="skeleton-loader__wave"></div>
      <div class="skeleton-cols__wrapper">
        <div
          v-for="i in listItemsAmount('column')"
          :key="`column-skeleton-${i}`"
          class="skeleton-cols__column"
        >
          <div class="content-item"></div>
        </div>
      </div>
    </div>
    <div v-else-if="type === 'line'" class="skeleton__line line">
      <div v-if="loading" class="skeleton-loader__wave"></div>
      <div class="line-item"></div>
    </div>
    <div
      v-if="isValidType('avatar')"
      class="skeleton__avatar"
      :style="{ width, height }"
    >
      <div v-for="i in listItemsAmount('avatar')" :key="i" class="avatar-item">
        <div v-if="loading" class="skeleton-loader__wave"></div>
        <div class="avatar-item__logo content-item"></div>
        <div class="avatar-item__content">
          <div class="content-item"></div>
          <div class="content-item"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* 
  ?type - optional 
  -n - amount of elements 
 
  Skeleton loader types: 
    - card 
    - list-items-n ?info 
    - avatar-n 
    - solo
    - line
    - two-column
*/
export default {
  props: {
    width: {
      type: String,
      default: "inherit",
    },
    height: {
      type: String,
      default: "max-content",
    },
    type: {
      type: String,
      default: "solo",
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    isValidType(prefix) {
      return this.type.includes(prefix);
    },
    listItemsAmount(prefix) {
      const regex = new RegExp(`${prefix}-(\\d+).*`);
      const amount = parseInt(this.type.replace(regex, "$1"), 10);
      return amount || 0;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/colors.scss";

@keyframes wave {
  to {
    background-position: calc(100% + 100px) 0, 0 0;
  }
}

.content-item {
  height: 100%;
  background-color: $colorWhiteSmoke;
  border-radius: 3px;
}

.skeleton-loader {
  position: relative;
  width: 100%;
  display: flex;
  overflow: hidden;
  align-items: center;

  .skeleton {
    &__list-items {
      width: 100%;
      background-color: #fff;

      .list-item {
        position: relative;
        width: inherit;
        height: 64px;
        display: flex;
        align-items: center;

        &:last-child {
          .list-item__content {
            border-bottom: none;
          }
        }

        &__logo {
          min-width: 44px;
          height: 34px;
          margin: 0 13px 0 16px;
        }

        &__content {
          width: 100%;
          height: inherit;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-right: 16px;
          border-bottom: 1px solid $colorWhiteSmoke;

          div:first-child {
            width: 160px;
            height: 20px;
          }

          div:nth-child(2) {
            width: 72px;
            height: 20px;
          }
        }
      }
    }

    &__two-column {
      width: 100%;
      background-color: #fff;
    }
    &__line {
      width: 100%;
      background-color: #fff;
    }
    &__avatar {
      width: 100%;
      background-color: #fff;

      .avatar-item {
        position: relative;
        width: inherit;
        height: 64px;
        display: flex;
        align-items: center;
        border-bottom: 1px solid $colorWhiteSmoke;

        &:last-child {
          border-bottom: none;
        }

        &__logo {
          min-width: 40px;
          height: 40px;
          border-radius: 50%;
          margin: 0 12px;
        }

        &__content {
          width: 100%;
          height: inherit;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;

          div:first-child {
            width: 225px;
            height: 20px;
            margin-bottom: 3px;
          }

          div:nth-child(2) {
            width: 160px;
            height: 16px;
          }
        }
      }
    }

    &__card {
      min-height: 60px;
      border-radius: 9px;
      background-color: $colorWhiteSmoke;
      display: flex;
      align-items: center;

      .content-item {
        background-color: #e5e5e5;
      }

      .card {
        &__logo {
          margin-left: 14px;
          min-width: 36px;
          height: 36px;
        }

        &__content {
          width: 100%;
          height: 40px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 0 14px;

          div:first-child {
            width: 148px;
            height: 20px;
          }

          div:last-child {
            width: 85px;
            height: 16px;
          }
        }
      }
    }

    &__solo {
      .content-item {
        width: 72px;
        height: 20px;
      }
    }
  }

  &__wave {
    position: absolute;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    background-repeat: repeat-y;
    background-size: 100px;
    background-position: -100px 0;
    animation: wave 1s infinite ease-in;
  }
}

.two-column {
  padding: 10px 15px;
  &__content {
    width: 100%;
    height: inherit;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    div:first-child {
      width: 48%;
      background-color: #e5e5e5;
      height: 55px;
      margin-bottom: 3px;
    }

    div:nth-child(2) {
      width: 48%;
      background-color: #e5e5e5;
      height: 55px;
      margin-bottom: 3px;
      background-color: #e5e5e5;
    }
  }
}
.skeleton-cols {
  &__wrapper {
    display: flex;
    height: 100%;
    background-color: #fff;
  }

  &__column {
    flex: 1;
    padding: 14px 16px 12px;
    height: 100%;
    border-radius: 5px;
  }
}
.line {
  width: 100%;
  height: 25px;
  padding: 5px 0;
  &-item {
    width: 100%;
    height: 22px;
    background-color: #e5e5e5;
  }
}
</style>
